import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ModuleConfigProvider } from './context/ModuleConfigContext';
import { BookingDataProvider } from './context/BookingDataContext';
import { Toaster } from './components/ui/toaster';
import { Toaster as SonnerToaster } from './components/ui/sonner';
import { ErrorBoundary } from './components/ErrorBoundary';
import { NetworkBanner } from './components/NetworkStatus';
import './i18n';

// Screens
import LoginScreen from './screens/LoginScreen';
import ForgotPasswordScreen from './screens/ForgotPasswordScreen';
import RegisterScreen from './screens/RegisterScreen';
import AgentInvitationLanding from './screens/AgentInvitationLanding';
import CalendarScreen from './screens/CalendarScreen';
import BookingDetailScreen from './screens/BookingDetailScreen';
import BookingCreateScreen from './screens/BookingCreateScreen';
import BookingEditScreen from './screens/BookingEditScreen';
import AnalyticsScreen from './screens/AnalyticsScreen';
import ProfileScreen from './screens/ProfileScreen';
import OnboardingFormScreen from './screens/OnboardingFormScreen';
import LegalPage from './screens/LegalPage';
import TutorialVideosScreen from './screens/TutorialVideosScreen';
import HelpScreen from './screens/HelpScreen';
import RateInventoryScreen from './screens/RateInventoryScreen';
import AdminLoginScreen from './screens/admin/AdminLoginScreen';
import AdminDashboard from './screens/admin/AdminDashboard';
import AdminPropertyDetail from './screens/admin/AdminPropertyDetail';
import AdminCalendarScreen from './screens/admin/AdminCalendarScreen';
import AdminEmailScreen from './screens/admin/AdminEmailScreen';
import ConversationsInboxScreen from './screens/ConversationsInboxScreen';
import ConversationsComingSoonScreen from './screens/ConversationsComingSoonScreen';
import ConversationThreadScreen from './screens/ConversationThreadScreen';

// Components
import BottomNavigation from './components/BottomNavigation';
import AdminBottomNavigation from './components/AdminBottomNavigation';
import ModuleGate from './components/ModuleGate';
import PolicyAcceptanceModal from './components/PolicyAcceptanceModal';
import InstallPrompt from './components/InstallPrompt';

// Request permissions on app load
const requestPermissions = () => {
  // Request Notification permission
  if ('Notification' in window && Notification.permission === 'default') {
    Notification.requestPermission().then(permission => {
      console.log('Notification permission:', permission);
    });
  }
  
  // Request Speech/Audio permission by testing Web Speech API
  if ('speechSynthesis' in window) {
    // Speech API doesn't require explicit permission, but we initialize it
    const utterance = new SpeechSynthesisUtterance('');
    utterance.volume = 0;
    window.speechSynthesis.speak(utterance);
  }
};

// iter143 — Version-scoped policy acceptance memory.
//   Legacy boolean flag `policy_accepted='true'` never invalidated when a
//   newer policy was published → users were let through without re-accept.
//   New scheme:
//     • `policy_accepted_version`  = <latest_version accepted by this user>
//     • `policy_content_cache`     = last-fetched {version, privacy_policy,
//                                     terms_and_conditions, published_at}
//   Legacy `policy_accepted` key is left in place (we ignore it now) so a
//   downgrade doesn't kill the session.
const POLICY_LS_ACCEPTED_VERSION = 'policy_accepted_version';
const POLICY_LS_CONTENT_CACHE = 'policy_content_cache';

// iter179 — Legacy alias for the Chats gate. `ModuleGate` is the new
// generic route-level guard that consumes access + lock from the Admin
// Panel. Kept behaviour identical for chats to avoid regressions.
const ChatsGate = ({ children }) => <ModuleGate module="chats">{children}</ModuleGate>;

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isAdmin, loading } = useAuth();
  const location = useLocation();
  const [policyChecked, setPolicyChecked] = React.useState(false);
  const [policyAccepted, setPolicyAccepted] = React.useState(false);
  const [checkingPolicy, setCheckingPolicy] = React.useState(true);
  // iter143 — Fail-closed offline state. When the server is unreachable and
  // we can't confirm the latest version, we do NOT let the user in.
  const [policyVerifyError, setPolicyVerifyError] = React.useState(false);
  const [retryCounter, setRetryCounter] = React.useState(0);

  React.useEffect(() => {
    if (!isAuthenticated || loading || isAdmin) {
      setCheckingPolicy(false);
      return;
    }

    // iter143 — Always ask the server on every login. LS is now only a
    // *hint* for optimistic UI (see below); the truth is what /legal/check
    // returns. No permanent bypass.
    // iter145 — Use a `cancelled` guard instead of AbortController so an
    // in-flight fetch is allowed to complete even if the effect re-runs
    // (React StrictMode / dependency flips on first login). Bumped the
    // timeout to 12s to comfortably cover the backend's 8s Admin-Panel
    // proxy budget.
    let cancelled = false;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 12_000);
    const checkPolicy = async () => {
      setPolicyVerifyError(false);
      try {
        const { getToken } = require('./services/tokenService');
        const token = getToken();
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/legal/check`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });
        const data = await res.json();
        if (cancelled) return;
        const accepted = data.RESULT?.accepted === true;
        const latestVersion = data.RESULT?.latest_version || '';

        setPolicyAccepted(accepted);
        // Persist the version the user is now considered "good for". A future
        // publish will change `latest_version` and this LS entry will no
        // longer match → modal re-appears automatically.
        if (accepted && latestVersion) {
          localStorage.setItem(POLICY_LS_ACCEPTED_VERSION, latestVersion);
        }
      } catch {
        if (cancelled) return;
        // iter143 — Fail CLOSED. We do NOT let the user in when we cannot
        // verify the latest policy version. Optimistic hint: if this user
        // previously accepted *some* version, we still show the blocker
        // (there might be a newer version we can't see); we only skip the
        // blocker when we can *prove* acceptance via the server.
        setPolicyVerifyError(true);
        setPolicyAccepted(false);
      } finally {
        clearTimeout(timeoutId);
        if (!cancelled) {
          setPolicyChecked(true);
          setCheckingPolicy(false);
        }
      }
    };
    checkPolicy();
    return () => { cancelled = true; clearTimeout(timeoutId); };
  }, [isAuthenticated, loading, isAdmin, retryCounter]);

  if (loading || checkingPolicy) {
    return (
      <div className="min-h-screen bg-dark-bg flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-neon-cyan border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Internal admin (BFR) accounts never use the owner app — send to admin console.
  if (isAdmin) {
    return <Navigate to="/admin" replace />;
  }

  // iter143 — Fail-closed offline blocker. Shown ONLY when /legal/check
  // could not be verified in this session. Provides a retry that re-runs
  // the effect (via retryCounter bump).
  if (policyVerifyError) {
    return (
      <div
        className="fixed inset-0 z-[300] bg-white flex flex-col items-center justify-center p-6 text-center"
        data-testid="policy-offline-blocker"
      >
        <div className="w-14 h-14 rounded-2xl bg-amber-100 flex items-center justify-center mb-4">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-7 h-7 text-amber-600">
            <path d="M1 1l22 22M16.72 11.06A10.94 10.94 0 0119 12.55M5 12.55a10.94 10.94 0 015.17-2.39M10.71 5.05A16 16 0 0122.58 9M1.42 9a15.91 15.91 0 014.7-2.88M8.53 16.11a6 6 0 016.95 0M12 20h.01" />
          </svg>
        </div>
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Can&apos;t reach the server</h2>
        <p className="text-sm text-gray-600 max-w-xs mb-6">
          Unable to verify the latest Privacy Policy &amp; Terms. Please connect to the internet and try again.
        </p>
        <button
          onClick={() => setRetryCounter((n) => n + 1) || setCheckingPolicy(true)}
          className="px-5 py-2.5 rounded-xl bg-[#1B2A7B] text-white text-sm font-medium hover:bg-[#152163] active:scale-[0.98] transition"
          data-testid="policy-offline-retry"
        >
          Retry
        </button>
      </div>
    );
  }

  // Show policy acceptance modal if not accepted
  if (policyChecked && !policyAccepted) {
    return (
      <PolicyAcceptanceModal
        onAccepted={(acceptedVersion) => {
          // iter143 — Persist the SPECIFIC version we accepted, not a bool.
          if (acceptedVersion) {
            localStorage.setItem(POLICY_LS_ACCEPTED_VERSION, acceptedVersion);
          }
          setPolicyAccepted(true);
        }}
      />
    );
  }

  return children;
};

// Admin (BFR) Route wrapper — only internal admin role may enter.
const AdminRoute = ({ children }) => {
  const { isAuthenticated, isAdmin, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-amber-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!isAuthenticated || !isAdmin) {
    return <Navigate to="/admin-login" replace />;
  }
  return children;
};

// Main Layout with Bottom Navigation
const MainLayout = ({ children }) => {
  const location = useLocation();
  const hideNavRoutes = ['/login', '/forgot-password', '/register', '/booking/new', '/onboarding'];
  const showNav = !hideNavRoutes.includes(location.pathname) && !location.pathname.startsWith('/booking/');

  return (
    <div className="min-h-screen bg-dark-bg">
      {children}
      {showNav && <BottomNavigation />}
    </div>
  );
};

// iter160 — Admin layout: renders AdminBottomNavigation across every /admin/*
// screen. Bottom nav replaces the previous dashboard tiles for Calendar +
// Email navigation.
const AdminLayout = ({ children }) => (
  <div className="min-h-screen bg-white">
    {children}
    <AdminBottomNavigation />
  </div>
);

// App Routes
const AppRoutes = () => {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<LoginScreen />} />
      <Route path="/forgot-password" element={<ForgotPasswordScreen />} />
      <Route path="/register" element={<RegisterScreen />} />
      <Route path="/privacy-policy" element={<LegalPage type="privacy" />} />
      <Route path="/terms-and-conditions" element={<LegalPage type="terms" />} />
      <Route path="/agent-invitation/:token" element={<AgentInvitationLanding />} />
      {/* iter162 — Profile menu destinations. Owner-facing pages gated by ProtectedRoute + MainLayout. */}
      <Route path="/tutorial-videos" element={<ProtectedRoute><MainLayout><TutorialVideosScreen /></MainLayout></ProtectedRoute>} />
      <Route path="/help" element={<ProtectedRoute><MainLayout><HelpScreen /></MainLayout></ProtectedRoute>} />

      {/* Internal Admin (BFR) — separate console with its own bottom nav. */}
      <Route path="/admin-login" element={<AdminLoginScreen />} />
      <Route path="/admin" element={<AdminRoute><AdminLayout><AdminDashboard /></AdminLayout></AdminRoute>} />
      <Route path="/admin/calendar" element={<AdminRoute><AdminLayout><AdminCalendarScreen /></AdminLayout></AdminRoute>} />
      <Route path="/admin/email" element={<AdminRoute><AdminLayout><AdminEmailScreen /></AdminLayout></AdminRoute>} />
      <Route path="/admin/property/:propertyId" element={<AdminRoute><AdminLayout><AdminPropertyDetail /></AdminLayout></AdminRoute>} />

      {/* Protected Routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CalendarScreen />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/calendar"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CalendarScreen />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/booking/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <BookingCreateScreen />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute>
            <MainLayout>
              <OnboardingFormScreen />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/booking/:bookingId/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <BookingEditScreen />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/booking/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <BookingDetailScreen />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics"
        element={
          <ProtectedRoute>
            <ModuleGate module="analytics">
              <MainLayout>
                <AnalyticsScreen />
              </MainLayout>
            </ModuleGate>
          </ProtectedRoute>
        }
      />
      <Route
        path="/rate-inventory"
        element={
          <ProtectedRoute>
            <ModuleGate module="rate_inventory">
              <MainLayout>
                <RateInventoryScreen />
              </MainLayout>
            </ModuleGate>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ProfileScreen />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/conversations"
        element={
          <ProtectedRoute>
            <ChatsGate>
              <MainLayout>
                <ConversationsInboxScreen />
              </MainLayout>
            </ChatsGate>
          </ProtectedRoute>
        }
      />
      <Route
        path="/conversations/:bookingId"
        element={
          <ProtectedRoute>
            <ChatsGate>
              <MainLayout>
                <ConversationThreadScreen />
              </MainLayout>
            </ChatsGate>
          </ProtectedRoute>
        }
      />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  // Request permissions on app load; also dismiss the splash screen.
  useEffect(() => {
    requestPermissions();
    // Signal to index.html that React has mounted → fade splash
    if (typeof window !== 'undefined' && typeof window.__shDismissSplash === 'function') {
      // Give the first paint a tick so the UI doesn't flash
      setTimeout(() => window.dispatchEvent(new Event('app-ready')), 300);
    }
  }, []);

  return (
    <BrowserRouter>
      <ErrorBoundary>
        <AuthProvider>
          <ModuleConfigProvider>
            <BookingDataProvider>
              <NetworkBanner />
              <AppRoutes />
              <InstallPrompt />
              <Toaster />
              <SonnerToaster />
            </BookingDataProvider>
          </ModuleConfigProvider>
        </AuthProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
}

export default App;
